import { useEffect, useState } from 'react';
import { Activity } from 'lucide-react';
import { useSiteDashboard } from '@/hooks/useSiteDashboard';
import { useDashboardRecentSessions } from '@/hooks/useDashboardRecentSessions';
import {
  SESSIONS_VIEWS, formatElapsed, readSessionsWidgetView, sessionsWidgetEmptyCopy, sessionsWidgetFooter,
  writeSessionsWidgetView, type SessionsView,
} from '@/lib/sessionsViews';
import { formatCurrency, formatRelativeTime } from './_lib';
import { recentSessionStatusClass, recentSessionStatusLabel } from './RecentSessionsWidget';
import styles from '../DashboardPage.module.css';

/** How often a session in progress re-reads the clock for its elapsed time. A render, never a fetch. */
const ELAPSED_TICK_MS = 30_000;

function browserStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * Sessions widget (Warren, 28 Oct 2026), in Start timeouts' cell on the
 * EVI team's dashboard: a toggle between All sessions (the latest 10 of
 * every status, the Recent sessions rows) and Active sessions (the latest
 * 10 in progress). It opens on Active until the viewer picks a view, then
 * on the last one picked in this browser (lib/sessionsViews.ts
 * readSessionsWidgetView), so it does not repeat the Recent sessions
 * widget beside it. Each row opens its session; the footer opens the
 * Sessions page on the same tab (/sessions, /sessions?tab=active). A
 * session in progress shows how long it has run and the kWh so far in
 * place of the amount. Live through the recent-sessions keys
 * (lib/sessionsViews.ts recentSessionsQuery). `activeTotal` is the
 * operator summary's in-progress count, the Active sessions tile's number.
 */
export default function SessionsWidget({ onNavigate, activeTotal }: { onNavigate?: (to: string) => void; activeTotal?: number }) {
  const isSite = useSiteDashboard();
  const Row = isSite ? 'div' : 'a';
  const [view, setView] = useState<SessionsView>(() => readSessionsWidgetView(browserStorage()));
  const q = useDashboardRecentSessions(view);
  const data = q.data;
  const footer = sessionsWidgetFooter(view, activeTotal);

  // Elapsed times move on their own between live refetches.
  const [, setTick] = useState(0);
  const hasLive = data?.rows.some((r) => r.status === 'active') ?? false;
  useEffect(() => {
    if (!hasLive) return undefined;
    const id = window.setInterval(() => setTick((n) => n + 1), ELAPSED_TICK_MS);
    return () => window.clearInterval(id);
  }, [hasLive]);
  const now = Date.now();

  const select = (next: SessionsView) => {
    setView(next);
    writeSessionsWidgetView(browserStorage(), next);
  };
  const go = (to: string) => (e: React.MouseEvent<HTMLElement>) => {
    if (!isSite && onNavigate) {
      e.preventDefault();
      onNavigate(to);
    }
  };

  return (
    <div className={styles.chartBody}>
      <div className={`${styles.chartHeader} ${styles.sessionsHeader}`}>
        <div className={styles.chartIcon}><Activity size={14} /></div>
        <p className={styles.chartTitle}>Sessions</p>
        {/* Two toggle buttons, not tabs: aria-pressed marks the view shown.
            At least 24 px tall, a 44 px target on a touch screen. */}
        <div className={styles.topRevToggle} role="group" aria-label="Sessions to show">
          {SESSIONS_VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              aria-pressed={view === v.id}
              className={`${styles.topRevToggleBtn} ${styles.sessionsToggleBtn} ${view === v.id ? styles.topRevToggleBtnActive : ''}`}
              onClick={() => select(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && data.rows.length === 0 && (
        <div className={styles.anomEmpty}>
          <span className={styles.anomEmptyIcon}><Activity size={18} /></span>
          <p>{sessionsWidgetEmptyCopy(view, data.is_scoped)}</p>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className={styles.recentSessionList}>
          {data.rows.map((row) => {
            const live = row.status === 'active';
            const elapsed = live ? formatElapsed(row.started_at, now) : '';
            return (
              <Row
                key={row.session_id}
                href={isSite ? undefined : `/sessions/${row.session_id}`}
                onClick={go(`/sessions/${row.session_id}`)}
                className={styles.recentSessionRow}
                style={isSite ? { cursor: 'default' } : undefined}
                title={live ? `Started ${new Date(row.started_at).toLocaleString()}, charging for ${elapsed}` : undefined}
              >
                <div className={styles.recentSessionBody}>
                  <span className={styles.recentSessionTitleLine}>
                    {row.charger_identity}
                    {row.location_name ? ` · ${row.location_name}` : ''}
                  </span>
                  <span className={styles.recentSessionMetaLine}>
                    {!isSite && `${row.driver_name} · `}
                    {live
                      ? `${row.energy_kwh.toFixed(1)} kWh so far`
                      : `${formatRelativeTime(row.started_at)} · ${row.energy_kwh.toFixed(1)} kWh`}
                  </span>
                </div>
                <span className={styles.recentSessionAmount}>
                  {live ? elapsed : formatCurrency(row.total_cost_cents / 100)}
                </span>
                <span className={`${styles.recentSessionStatusPill} ${recentSessionStatusClass(row.status)}`}>
                  {recentSessionStatusLabel(row.status)}
                </span>
              </Row>
            );
          })}
        </div>
      )}

      {data && !isSite && (
        <p className={styles.anomFooter}>
          <a className={styles.anomFooterLink} href={footer.href} onClick={go(footer.href)}>
            {footer.label}
          </a>
        </p>
      )}
    </div>
  );
}
